/**
 * useTransformChain - Transform chain operations for BufferSyncContext
 *
 * Extracted from BufferSyncContext.tsx. Manages the transform lifecycle:
 * transformAndCommit, refreshTransformChain, switchToChainVersion.
 *
 * @module @humanizer/studio/hooks/useTransformChain
 */

import { useCallback, type Dispatch, type SetStateAction, type MutableRefObject } from 'react';
import type { ContentItem } from '../contexts/buffer-sync-types';
import {
  bufferDataToContentItem,
  bufferTextToContentItems,
} from '../utils/buffer-bridge';
import { createLogger } from '../utils/debug';

const log = createLogger('TransformChain');

export interface TransformChainDeps {
  isElectron: boolean;
  sessionId: string | null;
  api: {
    transform: {
      humanize: (text: string, intensity: 'light' | 'moderate' | 'aggressive') => Promise<{ text?: string; humanizedText?: string }>;
      persona: (text: string, presetId: string) => Promise<{ text?: string; humanizedText?: string }>;
      personaCustom: (text: string, persona: { name: string; systemPrompt: string }) => Promise<{ text?: string; humanizedText?: string }>;
      style: (text: string, presetId: string) => Promise<{ text?: string; humanizedText?: string }>;
      styleCustom: (text: string, style: { name: string; stylePrompt: string }) => Promise<{ text?: string; humanizedText?: string }>;
    };
  };
  activeBufferIdRef: MutableRefObject<string | null>;
  workingContent: ContentItem[];
  setWorkingContent: Dispatch<SetStateAction<ContentItem[]>>;
  setIsDirty: Dispatch<SetStateAction<boolean>>;
  setContent: (content: ContentItem[]) => Promise<void>;
  commit: (message: string) => Promise<void>;
  transformChain: Array<{ id: string; label: string; type: string; timestamp: number }>;
  setTransformChain: Dispatch<SetStateAction<Array<{ id: string; label: string; type: string; timestamp: number }>>>;
  setActiveChainBufferId: Dispatch<SetStateAction<string | null>>;
}

export interface TransformChainReturn {
  transformAndCommit: (transformType: string, params: Record<string, unknown>) => Promise<void>;
  refreshTransformChain: () => Promise<void>;
  switchToChainVersion: (bufferId: string) => Promise<void>;
}

export function useTransformChain(deps: TransformChainDeps): TransformChainReturn {
  const {
    isElectron,
    sessionId,
    api,
    activeBufferIdRef,
    workingContent,
    setWorkingContent,
    setIsDirty,
    setContent,
    commit,
    setTransformChain,
    setActiveChainBufferId,
  } = deps;

  const transformAndCommit = useCallback(async (
    transformType: string,
    params: Record<string, unknown>
  ) => {
    if (!sessionId) throw new Error('Not connected');

    // ELECTRON: Transform via buffer IPC (routes through Ollama/BYOK/Cloud)
    if (isElectron) {
      if (!window.electronBuffer || !activeBufferIdRef.current) {
        log.warn('No buffer service or active buffer for transform');
        return;
      }

      // Map transform types to buffer operation types
      const operationTypeMap: Record<string, string> = {
        humanize: 'rewrite_humanize',
        persona: 'rewrite_persona',
        style: 'rewrite_persona',
      };

      try {
        const newBuffer = await window.electronBuffer.transform(
          activeBufferIdRef.current,
          {
            type: operationTypeMap[transformType] || 'transform_custom',
            parameters: params,
            description: `Transform: ${transformType}`,
          }
        );

        // Update UI with transformed content
        activeBufferIdRef.current = newBuffer.id;
        const transformedItem = bufferDataToContentItem(newBuffer);
        transformedItem.type = 'transformed';
        transformedItem.metadata.transformHistory = [{
          type: transformType,
          timestamp: Date.now(),
        }];

        setWorkingContent([transformedItem]);
        setIsDirty(false);
        setActiveChainBufferId(newBuffer.id);

        // Refresh transform chain from new buffer
        try {
          const chain = await window.electronBuffer.getChain(newBuffer.id);
          setTransformChain(chain);
        } catch {
          // Chain fetch is best-effort
        }
      } catch (err) {
        log.error('Electron transform failed:', err);
        throw err;
      }
      return;
    }

    const text = workingContent.map(item => item.text).join('\n\n');

    let result: { text?: string; humanizedText?: string } | undefined;

    switch (transformType) {
      case 'humanize':
        result = await api.transform.humanize(text, (params.intensity as 'light' | 'moderate' | 'aggressive') ?? 'moderate');
        break;
      case 'persona':
        if (params.customPersona) {
          result = await api.transform.personaCustom(text, params.customPersona as { name: string; systemPrompt: string });
        } else {
          result = await api.transform.persona(text, params.presetId as string);
        }
        break;
      case 'style':
        if (params.customStyle) {
          result = await api.transform.styleCustom(text, params.customStyle as { name: string; stylePrompt: string });
        } else {
          result = await api.transform.style(text, params.presetId as string);
        }
        break;
    }

    const transformedText = result?.humanizedText ?? result?.text;
    if (!transformedText) return;

    const transformedItem: ContentItem = {
      id: crypto.randomUUID(),
      type: 'transformed',
      text: transformedText,
      metadata: {
        transform: { type: transformType, ...params },
        timestamp: Date.now(),
        wordCount: transformedText.split(/\s+/).filter(Boolean).length,
      },
    };

    await setContent([transformedItem]);
    await commit(`Transform: ${transformType}`);
  }, [api, sessionId, workingContent, setContent, commit, isElectron, activeBufferIdRef, setWorkingContent, setIsDirty, setTransformChain, setActiveChainBufferId]);

  // Refresh transform chain from the current active buffer
  const refreshTransformChain = useCallback(async () => {
    if (!isElectron || !window.electronBuffer || !activeBufferIdRef.current) {
      setTransformChain([]);
      setActiveChainBufferId(null);
      return;
    }
    try {
      const chain = await window.electronBuffer.getChain(activeBufferIdRef.current);
      setTransformChain(chain);
      setActiveChainBufferId(activeBufferIdRef.current);
    } catch {
      setTransformChain([]);
      setActiveChainBufferId(null);
    }
  }, [isElectron, activeBufferIdRef, setTransformChain, setActiveChainBufferId]);

  // Switch to a specific version in the transform chain
  const switchToChainVersion = useCallback(async (bufferId: string) => {
    if (!isElectron || !window.electronBuffer) return;

    try {
      const buffer = await window.electronBuffer.load(bufferId);
      if (!buffer) return;

      activeBufferIdRef.current = buffer.id;
      const items = bufferTextToContentItems(buffer.text, buffer.origin);
      setWorkingContent(items);
      setActiveChainBufferId(bufferId);
      setIsDirty(false);
    } catch (err) {
      log.error('switchToChainVersion failed:', err);
    }
  }, [isElectron, activeBufferIdRef, setWorkingContent, setActiveChainBufferId, setIsDirty]);

  return {
    transformAndCommit,
    refreshTransformChain,
    switchToChainVersion,
  };
}
